import React from 'react';
import TimezoneSelector from './TimezoneSelector';

/**
 * Presets preconfigurados para filtrado por partido / sesión
 */
const PRESETS = [
  { key: 'today', label: 'Partido de Hoy', icon: 'bi-sun' },
  { key: 'yesterday', label: 'Partido de Ayer', icon: 'bi-calendar-check' },
  { key: 'last6hours', label: 'Últimas 6 Horas', icon: 'bi-hourglass-split' },
  { key: 'custom', label: 'Personalizado', icon: 'bi-sliders' },
];

/**
 * Formatea una marca UTC ISO a formato legible (YYYY-MM-DD HH:mm UTC).
 * @param {string|null} utcIsoString
 * @returns {string}
 */
const formatUtcDisplay = (utcIsoString) => {
  if (!utcIsoString) return '--';
  try {
    const d = new Date(utcIsoString);
    if (isNaN(d.getTime())) return '--';
    const iso = d.toISOString();
    return `${iso.slice(0, 10)} ${iso.slice(11, 16)} UTC`;
  } catch {
    return '--';
  }
};

/**
 * Barra de control y filtrado temporal de reportes.
 * Permite alternar entre métricas acumuladas y filtro acotado por partido o rango personalizado,
 * sincronizando automáticamente las marcas locales con sus equivalentes UTC exactos.
 *
 * @param {Object} props
 * @param {Object} props.filterState - Estado centralizado del filtro
 * @param {Function} props.onModeChange - Callback para cambio de modo ('all' | 'match')
 * @param {Function} props.onPresetChange - Callback para cambio de preset ('today' | 'yesterday' | 'last6hours' | 'custom')
 * @param {Function} props.onDateChange - Callback para cambio en inputs datetime ('startLocal' | 'endLocal', valor)
 * @param {Function} [props.onTimezoneChange] - Callback para cambio de zona horaria
 * @param {string} [props.detectedTimezone] - Zona horaria detectada en el dispositivo
 * @param {React.ReactNode} [props.timezoneSelector] - Componente selector inyectado opcional
 */
export default function DateRangeFilter({
  filterState,
  onModeChange,
  onPresetChange,
  onDateChange,
  onTimezoneChange,
  detectedTimezone,
  timezoneSelector,
}) {
  const { mode, timezone, preset, startLocal, endLocal, startUtc, endUtc, isValid } = filterState;

  const isMatchMode = mode === 'match';
  const isCustom = preset === 'custom';

  return (
    <section className="AdmReports__FilterBar" aria-label="Filtro de rango de fechas para reportes">
      {/* Fila Superior: Selector de Modo y Selector de Zona Horaria */}
      <div className="AdmReports__FilterBar__TopRow">
        <div className="AdmReports__ModeSwitch" role="group" aria-label="Modo de consulta de reportes">
          <button
            type="button"
            className={`AdmReports__ModeBtn ${!isMatchMode ? 'AdmReports__ModeBtn--Active' : ''}`}
            onClick={() => onModeChange('all')}
            aria-pressed={!isMatchMode}
          >
            <i className="bi bi-collection-fill me-1" aria-hidden="true"></i>
            Todo lo acumulado
          </button>
          <button
            type="button"
            className={`AdmReports__ModeBtn ${isMatchMode ? 'AdmReports__ModeBtn--Active' : ''}`}
            onClick={() => onModeChange('match')}
            aria-pressed={isMatchMode}
          >
            <i className="bi bi-calendar2-range-fill me-1" aria-hidden="true"></i>
            Filtrar por Partido
          </button>
        </div>

        {/* Zona horaria: Componente inyectado o renderizado directo */}
        <div className="AdmReports__FilterBar__TzContainer">
          {timezoneSelector ? (
            timezoneSelector
          ) : (
            <TimezoneSelector
              currentTimezone={timezone}
              onChangeTimezone={onTimezoneChange}
              detectedTimezone={detectedTimezone}
            />
          )}
        </div>
      </div>

      {/* Controles de Rango de Partido (visibles en modo 'match') */}
      {isMatchMode && (
        <div className="AdmReports__MatchControls">
          {/* Fila de Botones Preset */}
          <div className="AdmReports__PresetsRow">
            <span className="AdmReports__PresetsLabel">
              <i className="bi bi-lightning-charge-fill me-1" aria-hidden="true"></i>
              Presets rápidos:
            </span>
            <div className="AdmReports__PresetsGroup" role="group" aria-label="Presets de fecha">
              {PRESETS.map((p) => {
                const isActive = preset === p.key;
                return (
                  <button
                    key={p.key}
                    type="button"
                    className={`AdmReports__PresetBtn ${isActive ? 'AdmReports__PresetBtn--Active' : ''}`}
                    onClick={() => onPresetChange(p.key)}
                    aria-pressed={isActive}
                  >
                    <i className={`bi ${p.icon} me-1`} aria-hidden="true"></i>
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Fila de Inputs Datetime-Local */}
          <div className="AdmReports__InputsRow">
            <div className="AdmReports__InputGroup">
              <label htmlFor="report-start-datetime" className="AdmReports__InputLabel">
                <i className="bi bi-calendar-event me-1" aria-hidden="true"></i>
                Inicio del Partido (Hora Local)
              </label>
              <input
                id="report-start-datetime"
                type="datetime-local"
                className={`AdmReports__DateInput ${!isValid ? 'AdmReports__DateInput--Error' : ''}`}
                value={startLocal || ''}
                onChange={(e) => onDateChange('startLocal', e.target.value)}
                aria-invalid={!isValid}
                aria-describedby={!isValid ? 'report-date-error-desc' : undefined}
                title={isCustom ? 'Edita la fecha y hora inicial' : 'Valor calculado según preset seleccionado'}
              />
            </div>

            <div className="AdmReports__InputGroup">
              <label htmlFor="report-end-datetime" className="AdmReports__InputLabel">
                <i className="bi bi-calendar2-check me-1" aria-hidden="true"></i>
                Fin del Partido (Hora Local)
              </label>
              <input
                id="report-end-datetime"
                type="datetime-local"
                className={`AdmReports__DateInput ${!isValid ? 'AdmReports__DateInput--Error' : ''}`}
                value={endLocal || ''}
                onChange={(e) => onDateChange('endLocal', e.target.value)}
                aria-invalid={!isValid}
                aria-describedby={!isValid ? 'report-date-error-desc' : undefined}
                title={isCustom ? 'Edita la fecha y hora final' : 'Valor calculado según preset seleccionado'}
              />
            </div>
          </div>
        </div>
      )}

      {/* Resumen Visual en Badge */}
      {!isMatchMode ? (
        <div className="AdmReports__SummaryBadge AdmReports__SummaryBadge--All">
          <i className="bi bi-infinity AdmReports__SummaryBadge__Icon" aria-hidden="true"></i>
          <div className="AdmReports__SummaryBadge__Content">
            <strong>Histórico Acumulado Completo:</strong> Las consultas y exportaciones incluirán todos los
            registros generados en la plataforma sin límite temporal.
          </div>
        </div>
      ) : isValid ? (
        <div className="AdmReports__SummaryBadge AdmReports__SummaryBadge--Match">
          <div className="AdmReports__SummaryBadge__Item">
            <i className="bi bi-clock-history AdmReports__SummaryBadge__ItemIcon" aria-hidden="true"></i>
            <span className="AdmReports__SummaryBadge__Label">Rango Local:</span>
            <strong className="AdmReports__SummaryBadge__Value">
              {startLocal ? startLocal.replace('T', ' ') : '--'}
              <span className="mx-1 text-muted">&rarr;</span>
              {endLocal ? endLocal.replace('T', ' ') : '--'}
            </strong>
          </div>

          <div className="AdmReports__SummaryBadge__Divider" aria-hidden="true"></div>

          <div className="AdmReports__SummaryBadge__Item">
            <i className="bi bi-cloud-arrow-up-fill AdmReports__SummaryBadge__ItemIcon text-success" aria-hidden="true"></i>
            <span className="AdmReports__SummaryBadge__Label">Marca UTC (Servidor):</span>
            <code className="AdmReports__SummaryBadge__Utc">
              {formatUtcDisplay(startUtc)} &rarr; {formatUtcDisplay(endUtc)}
            </code>
          </div>
        </div>
      ) : (
        <div
          id="report-date-error-desc"
          className="AdmReports__SummaryBadge AdmReports__SummaryBadge--Error"
          role="alert"
        >
          <i className="bi bi-exclamation-triangle-fill AdmReports__SummaryBadge__Icon" aria-hidden="true"></i>
          <div className="AdmReports__SummaryBadge__Content">
            <strong>Rango no válido:</strong> La fecha/hora de fin debe ser posterior a la de inicio. Corrige
            los valores para habilitar las consultas.
          </div>
        </div>
      )}
    </section>
  );
}

export { DateRangeFilter };
